import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { api } from '../lib/api';
import { belgeMi, FotoBuyut } from '../lib/foto-goster';

/**
 * GL-03 — ARAMA & ARŞİV (devir dokumani Bolum 6.9).
 *
 * K8'in kullaniciya gorunen yuzu. Her sonuc hangi ISE, hangi ADIMA ve
 * NE ZAMANA ait oldugunu tasir — basibos galeri yok.
 *
 * ⚠️ SONUCA TIKLAMAK FOTOGRAFI ACAR, ise GOTURMEZ (kullanici bildirimi
 *    07.08.2026: "fotografla acilmiyor, tiklandiginda ilgili panoya
 *    yonlendiriyor"). Burasi bir ARAMA ekrani: kullanici fotografi gormek
 *    icin ariyor. Ise gitmek ISTEGE BAGLI ve buyutme katmaninin icindeki
 *    dugmeyle yapiliyor — yani once kanit, sonra baglam.
 */

interface Sonuc {
  fotoNo: number;
  step: string;
  stepEtiket: string;
  cekilmeZamani: string;
  ceken: string | null;
  jobId: string;
  isEmriNo: string | null;
  /** Aynı sipariş altında birden çok iskele olabiliyor — ayırt eden alan. */
  iskeleOnayNo: string | null;
  tanim: string;
  unite: string;
  dosyaVar: boolean;
  /** PDF (çok sayfalı belge) küçük görsel taşımaz — kutucuk olarak çizilir. */
  contentType?: string | null;
}

const SEKMELER = [
  { d: 'foto', e: 'Fotolar' },
  { d: 'is_emri', e: 'İş emirleri' },
  { d: 'izin', e: 'İzin formları' },
  { d: 'kontrol', e: 'Kontrol formları' },
  /* Söküm adımları "Fotolar"da zaten vardı ama kendi filtresi yoktu —
     100+ sonuç içinde bulunamıyordu (kullanıcı bildirimi 07.08.2026). */
  { d: 'sokum', e: 'Söküm' },
] as const;

export function Arsiv() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [tip, setTip] = useState<string>('foto');
  const [d, setD] = useState<{ sayi: number; sonuclar: Sonuc[] } | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  /** Büyütülen fotoğrafın numarası — null ise katman kapalı. */
  const [buyuk, setBuyuk] = useState<number | null>(null);

  const ara = useCallback(async () => {
    try {
      setD(
        await api.get<{ sayi: number; sonuclar: Sonuc[] }>(
          `/arsiv?tip=${tip}&q=${encodeURIComponent(q)}`,
        ),
      );
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Aranamadı');
    }
  }, [q, tip]);

  useEffect(() => {
    const t = setTimeout(() => void ara(), 250);
    return () => clearTimeout(t);
  }, [ara]);

  return (
    <div className="mx-auto max-w-6xl p-4 lg:p-6">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          className="min-w-64 flex-1 rounded-(--radius-mod) border border-line-2 px-3 py-2.5 text-sm outline-none focus:border-navy"
          placeholder="Sipariş no, iskele onay no, ünite veya iş tanımı ara…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
        />
        <div className="flex flex-wrap gap-1.5">
          {SEKMELER.map((s) => (
            <button
              key={s.d}
              onClick={() => setTip(s.d)}
              className={`rounded-(--radius-mod) border px-3 py-1.5 text-sm ${
                tip === s.d ? 'border-navy bg-navy text-white' : 'border-line-2 text-wait'
              }`}
            >
              {s.e}
            </button>
          ))}
        </div>
      </div>

      {hata && (
        <p className="mb-4 rounded-(--radius-mod) bg-crit/10 p-3 text-sm text-crit">{hata}</p>
      )}

      {d && (
        <>
          <p className="mb-3 text-sm text-wait">
            <span className="num">{d.sayi}</span> sonuç
            {q && (
              <>
                {' '}
                · <strong>{q}</strong> için
              </>
            )}
          </p>

          {d.sonuclar.length === 0 ? (
            <div className="rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod) p-10 text-center">
              <p className="font-medium text-ink-2">Sonuç yok</p>
              <p className="mt-1 text-sm text-wait">
                Sipariş numarası, <strong>iskele onay numarası</strong>, ünite adı ya da iş
                tanımıyla arayın.
              </p>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {d.sonuclar.map((s) => (
                <button
                  key={s.fotoNo}
                  /*
                   * ⚠️ BELGE BÜYÜTME KATMANINA GİRMEZ, yeni sekmede açılır.
                   *    Katman tek kareyi ölçekliyor; beş sayfalık bir PDF'te
                   *    ikinci sayfaya geçmenin yolu tarayıcının kendi
                   *    görüntüleyicisinden geçer.
                   */
                  onClick={() =>
                    belgeMi(s)
                      ? window.open(`/api/fotolar/${s.fotoNo}`, '_blank', 'noopener')
                      : setBuyuk(s.fotoNo)
                  }
                  disabled={!s.dosyaVar}
                  className="overflow-hidden rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod) text-left hover:border-navy"
                >
                  <div className="flex h-32 items-center justify-center bg-surface-2">
                    {!s.dosyaVar ? (
                      <span className="text-xs text-wait">dosya yok (test kaydı)</span>
                    ) : belgeMi(s) ? (
                      <span className="flex flex-col items-center gap-1 text-wait">
                        <span className="text-sm font-semibold tracking-wide">PDF</span>
                        <span className="text-xs">belge — açmak için tıkla</span>
                      </span>
                    ) : (
                      <img
                        src={`/api/fotolar/${s.fotoNo}`}
                        alt={`Foto ${s.fotoNo}`}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    )}
                  </div>
                  <div className="p-3">
                    <div className="flex items-center justify-between gap-2">
                      {/*
                        ⚠️ ONAY NUMARASI KÜÇÜK RESİMDE DE GÖRÜNÜR (istek 11.08.2026).
                        Önceden yalnız büyütme katmanındaydı; kullanıcı hangi
                        iskeleye ait olduğunu görmek için her kareye tıklamak
                        zorundaydı. Aynı sipariş altında 29 iskele olabiliyor ve
                        onları ayıran TEK alan bu — ızgarada okunmuyorsa arama
                        sonucu "hangisiydi" sorusunu cevaplamıyor demektir.

                        Numarası olmayan işte (teslim edilmemiş) yalnız foto no
                        yazar; "Onay —" yazmak boş bir söz olurdu.
                      */}
                      <span className="num truncate font-semibold text-navy">
                        {s.iskeleOnayNo && (
                          <>
                            Onay {s.iskeleOnayNo}
                            <span className="mx-1 font-normal text-wait">·</span>
                          </>
                        )}
                        Foto {s.fotoNo}
                      </span>
                      {/* Rozet daralmaz, uzun etiket başlıktan kısalır — adım adı
                          kırpılınca "Söküm iş emri" ile "Söküm" karışırdı. */}
                      <span className="shrink-0 rounded bg-surface-2 px-1.5 py-0.5 text-xs text-wait">
                        {s.stepEtiket}
                      </span>
                    </div>
                    {/* K8 — foto ASLA tek başına: iş + adım + zaman birlikte */}
                    <p className="num mt-1 text-xs text-ink-2">{s.isEmriNo ?? '—'}</p>
                    <p className="truncate text-xs text-wait">
                      {s.tanim} · {s.unite}
                    </p>
                    <p className="num text-xs text-wait">
                      {new Date(s.cekilmeZamani).toLocaleString('tr-TR', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}
                      {s.ceken && ` · ${s.ceken}`}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/*
        BÜYÜTME KATMANI — fotoğraf burada açılır, sayfa DEĞİŞMEZ.
        Oklarla sonuçlar arasında gezilir; alttaki özet hangi işe bakıldığını
        söyler ve "İşe git" isteğe bağlı bir adımdır (K8: foto asla tek başına).
      */}
      {buyuk !== null &&
        (() => {
          const acik = d?.sonuclar.find((x) => x.fotoNo === buyuk);
          if (!acik) return null;
          return (
            <FotoBuyut
              fotoNo={buyuk}
              /* Ok tuşlarıyla gezilen liste yalnız GÖRSELLER — PDF katmanda açılmıyor. */
              hepsi={(d?.sonuclar ?? [])
                .filter((x) => x.dosyaVar && !belgeMi(x))
                .map((x) => x.fotoNo)}
              onKapat={() => setBuyuk(null)}
              onDegis={setBuyuk}
              /* Kullanıcının okuduğu numara foto no değil ONAY no (11.08.2026).
                 Küçük görselde de aynı sırayla yazıyor — göz aynı yeri arıyor. */
              etiket={
                acik.iskeleOnayNo
                  ? `Onay ${acik.iskeleOnayNo} · Foto ${acik.fotoNo}`
                  : `Foto ${acik.fotoNo}`
              }
              ek={
                <div className="space-y-1.5">
                  <p className="font-semibold">{acik.tanim}</p>
                  <p className="num text-xs text-white/75">
                    {acik.isEmriNo ?? '—'}
                    {acik.iskeleOnayNo && ` · onay ${acik.iskeleOnayNo}`} · {acik.unite}
                  </p>
                  <p className="text-xs text-white/75">
                    {acik.stepEtiket} ·{' '}
                    <span className="num">
                      {new Date(acik.cekilmeZamani).toLocaleString('tr-TR', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}
                    </span>
                    {acik.ceken && ` · ${acik.ceken}`}
                  </p>
                  <button
                    type="button"
                    onClick={() =>
                      void navigate({ to: '/is/$isId', params: { isId: acik.jobId } })
                    }
                    className="mt-1 w-full rounded-(--radius-mod) bg-white/15 px-3 py-2 text-sm font-medium hover:bg-white/25"
                  >
                    İşe git →
                  </button>
                </div>
              }
            />
          );
        })()}

      <p className="mt-5 rounded-(--radius-mod) bg-surface-2 p-3 text-xs leading-relaxed text-wait">
        Her fotoğraf bir <strong>iş + adım + tarihe</strong> bağlıdır — başıboş galeri yok (K8).
        Arama <strong>sipariş</strong> ve <strong>iskele onay</strong> numarasıyla yapılır; bulunan
        numaraya ait fotoğraflar listelenir. Aynı sipariş altında birden çok iskele olabildiği için
        onları ayıran alan onay numarasıdır.
      </p>
    </div>
  );
}
